import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { LoginSecurity, OtpMedium } from '@common/types';
import { OtpFormValues, PhoneNumberFormValues } from '@elements';
import * as Sentry from '@sentry/react';
import insureAPIs, { OtpPayload } from '@services/apis/insure';
import { COUNTRY_INDIA, SMS } from '@utils/constants';

import { PolicyLists } from './types';

type State = {
  // whether user is logged in or not at the moment
  loggedIn: boolean;
  // any errors occurred during login
  error: { title: string; message: string } | string | null;
  // loader for fetching policies
  loadingPolicies: boolean;
  // list of policies for the user
  policies: PolicyLists;
  // otp sending loader
  sendingOtp: boolean;
  // login loader
  loggingIn: boolean;
  // to be used as initial values when user goes back to change phone
  loginDetails: PhoneNumberFormValues;
  // otp send status
  otpSent: boolean;
};

type Action = {
  // send otp to the users phone number
  sendOtp: (
    values: PhoneNumberFormValues,
    medium: OtpMedium,
    security: LoginSecurity,
    successCallback?: () => void,
    failureCallback?: (error: any) => void
  ) => Promise<void>;
  // login using otp
  login: (
    values: OtpFormValues,
    successCallback?: () => void,
    failureCallback?: (error: any) => void
  ) => Promise<void>;
  // fetch all the policies mapped to the phone number
  fetchPolicies: () => Promise<void>;
  // reset the login form to initial state
  resetLoginForm: (phone?: boolean) => void;
  // logout from the app
  logout: () => Promise<void>;
  // reset store to initial state
  reset: () => void;
};

const INIT_LOGIN_DETAILS = {
  country: COUNTRY_INDIA,
  mobile: ''
};

const initialState = {
  loggedIn: false,
  loading: false,
  error: null,
  policies: { health: [], term: [] },
  sendingOtp: false,
  loggingIn: false,
  otpSent: false,
  loginDetails: INIT_LOGIN_DETAILS,
  loadingPolicies: false
};

export const useAccount = create<State & Action>()(
  immer(
    devtools(
      /** Persist store , clean up incase of unauthorized api calls or logout */
      persist(
        (set, get) => ({
          ...initialState,
          sendOtp: async (
            values: PhoneNumberFormValues,
            medium: OtpMedium,
            security: LoginSecurity,
            successCallback,
            failureCallback
          ) => {
            set(
              (state) => {
                state.sendingOtp = true;
                state.error = null;
              },
              false,
              'sendOtp/start'
            );
            try {
              if (!values.mobile || !values.country.calling_code)
                throw new Error('No phone number');

              const apiPayload: OtpPayload = {
                phone_num: values.mobile,
                country_code: values.country.calling_code,
                ...security
              };

              const response =
                medium === SMS
                  ? await insureAPIs.sendOTP(apiPayload)
                  : await insureAPIs.sendWhatsAppOTP(apiPayload);
              console.debug('Sending otp');
              if (response && response.data.message === 'OTP sent successfully') {
                set(
                  (state) => {
                    state.loginDetails = values;
                    state.otpSent = true;
                  },
                  false,
                  'sendOtp/success'
                );
                successCallback && successCallback();
              } else {
                throw new Error(
                  `An error occurred while sending OTP, ${response?.data?.message}`
                );
              }
            } catch (error: any) {
              set(
                (state) => {
                  state.error = {
                    title: 'OTP not sent, Please try again!',
                    message:
                      'An error occurred while generating OTP, please try again. Make sure you are connected to the internet.'
                  };
                },
                false,
                'sendOtp/error'
              );
              failureCallback && failureCallback(error);
            } finally {
              set(
                (state) => {
                  state.sendingOtp = false;
                },
                false,
                'sendOtp/complete'
              );
            }
          },
          login: async (values: OtpFormValues, successCallback, failureCallback) => {
            set(
              (state) => {
                state.loggingIn = true;
                state.error = null;
              },
              false,
              'login/start'
            );
            const loginDetails = get().loginDetails;
            try {
              if (
                !values.otp ||
                !loginDetails.mobile ||
                !loginDetails.country.calling_code
              )
                throw new Error('Cannot verify the OTP');

              // Verify OTP for login
              const response = await insureAPIs.customerLogin({
                otp: values.otp,
                phone_num: loginDetails.mobile,
                country_code: loginDetails.country.calling_code
              });

              if (response && response?.data?.message === 'Successfully Logged in') {
                set(
                  (state) => {
                    state.loggedIn = true;
                    state.otpSent = false;
                  },
                  false,
                  'login/success'
                );
                successCallback && successCallback();
              } else {
                throw new Error(
                  'An error occurred while logging in : ' + response?.data?.message
                );
              }
            } catch (error: any) {
              Sentry.captureException(error);
              set(
                (state) => {
                  state.error = {
                    title: 'Login failed',
                    message: 'An error occurred while logging in please try again'
                  };
                },
                false,
                'login/error'
              );
              failureCallback && failureCallback(error);
            } finally {
              set(
                (state) => {
                  state.loggingIn = false;
                },
                false,
                'login/complete'
              );
            }
          },
          fetchPolicies: async () => {
            set(
              (state) => {
                state.loadingPolicies = true;
              },
              false,
              'fetchPolicies/start'
            );
            try {
              const response = await insureAPIs.fetchPolicies();
              set(
                (state) => {
                  state.policies = response.data;
                },
                false,
                'fetchPolicies/success'
              );
            } catch (err: any) {
              set(
                (state) => {
                  state.error = {
                    title: 'Policies',
                    message:
                      'An error occurred while fetching your policies, please try again later!'
                  };
                },
                false,
                'fetchPolicies/error'
              );
            } finally {
              set(
                (state) => {
                  state.loadingPolicies = false;
                },
                false,
                'fetchPolicies/complete'
              );
            }
          },
          resetLoginForm: (phone) => {
            set(
              (state) => {
                state.otpSent = false;
                if (phone) {
                  state.loginDetails.mobile = '';
                }
              },
              false,
              'resetLoginForm'
            );
          },
          logout: async () => {
            set(initialState, false, 'logout');
            try {
              await insureAPIs.customerLogout();
            } catch (err: any) {
              console.log(err);
            }
          },
          reset: () => {
            set(initialState, false, 'reset');
          }
        }),
        {
          name: 'ditto-account',
          partialize: (state) => ({ loggedIn: state.loggedIn })
        }
      )
    )
  )
);
